import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { Navigation, Truck, RefreshCw } from 'lucide-react';

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_MS = 20000; // 20 s fleet refresh

const legColor = { pickup: '#FFB800', transit: '#FF4500', delivery: '#00C853' };

const agentIcon = (color, label) =>
  L.divIcon({
    html: `<div style="
      background: ${color};
      border: 2px solid #000;
      width: 32px; height: 32px;
      border-radius: 0;
      display:flex; align-items:center; justify-content:center;
      font-weight:900; font-family:'IBM Plex Mono', monospace;
      font-size:12px; color:#000;
      box-shadow: 3px 3px 0px #0A0A0A;
    ">${label}</div>`,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

const FitToMarkers = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    if (positions.length === 1) {
      map.setView(positions[0], 11);
    } else {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, positions]);
  return null;
};

const PartnerFleetMap = ({ partnerCode }) => {
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const timerRef = useRef(null);

  const fetchFleet = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/partner/${partnerCode}/fleet-locations`);
      setFleet(data.fleet || []);
      setError(null);
      setRefreshedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load fleet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!partnerCode) return;
    fetchFleet();
    timerRef.current = setInterval(fetchFleet, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerCode]);

  const livePositions = fleet
    .filter(a => a.live && typeof a.lat === 'number' && typeof a.lng === 'number')
    .map(a => [a.lat, a.lng]);

  const totalActive = fleet.length;
  const totalLive = livePositions.length;

  return (
    <div className="card-brutalist p-0 overflow-hidden" data-testid="partner-fleet-map">
      <div className="px-6 py-4 border-b-2 border-black bg-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'IBM Plex Mono' }}>
            LIVE FLEET
          </h2>
          <div className="text-xs font-mono text-gray-600 mt-1">
            {totalLive}/{totalActive} AGENTS BEACONING
            {refreshedAt && <> · UPD {refreshedAt.toLocaleTimeString()}</>}
          </div>
        </div>
        <button
          data-testid="fleet-refresh-btn"
          onClick={fetchFleet}
          className="btn-secondary px-3 py-2 flex items-center gap-2 text-xs"
        >
          <RefreshCw size={14} />
          REFRESH
        </button>
      </div>

      <div style={{ height: '440px', width: '100%', position: 'relative' }}>
        <MapContainer
          center={[13.5, 78.5]}
          zoom={6}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {fleet.filter(a => a.live).map(a => {
            const primaryLeg = a.bookings?.[0]?.leg || 'pickup';
            return (
              <Marker
                key={a.agent_code}
                position={[a.lat, a.lng]}
                icon={agentIcon(legColor[primaryLeg] || '#FFB800', (a.agent_code || 'A').slice(-3))}
              >
                <Popup>
                  <strong>{a.agent_name || a.agent_code}</strong> ({a.agent_code})<br />
                  <span style={{ color: '#525252' }}>
                    {a.age_seconds != null ? `Updated ${a.age_seconds}s ago` : ''}
                  </span>
                  <hr style={{ margin: '4px 0' }} />
                  <strong>Active bookings ({a.bookings?.length || 0}):</strong>
                  <ul style={{ paddingLeft: 14, margin: '4px 0' }}>
                    {(a.bookings || []).slice(0, 4).map((b, i) => (
                      <li key={i} style={{ fontSize: 11 }}>
                        {b.booking_id?.slice(0, 8)} · {b.leg} · {b.tracking_status}
                      </li>
                    ))}
                  </ul>
                </Popup>
              </Marker>
            );
          })}
          <FitToMarkers positions={livePositions} />
        </MapContainer>
      </div>

      {/* Legend + table */}
      <div className="px-6 py-3 border-t-2 border-black bg-white flex flex-wrap gap-4 items-center text-xs font-mono">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: legColor.pickup }} />
          <span className="uppercase">Pickup</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: legColor.transit }} />
          <span className="uppercase">Transit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: legColor.delivery }} />
          <span className="uppercase">Delivery</span>
        </div>
      </div>

      <div className="overflow-x-auto border-t-2 border-black">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 border-b-2 border-black">
            <tr>
              <th className="text-left p-3 font-mono uppercase text-xs">Agent</th>
              <th className="text-left p-3 font-mono uppercase text-xs">Status</th>
              <th className="text-left p-3 font-mono uppercase text-xs">Assignments</th>
              <th className="text-left p-3 font-mono uppercase text-xs">Last ping</th>
            </tr>
          </thead>
          <tbody data-testid="fleet-table-body">
            {loading && (
              <tr><td colSpan={4} className="p-6 text-center text-gray-600 font-mono">Loading fleet…</td></tr>
            )}
            {!loading && fleet.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-gray-600 font-mono">
                No agents currently assigned to active bookings.
              </td></tr>
            )}
            {!loading && fleet.map(a => (
              <tr key={a.agent_code} className="border-b border-gray-200" data-testid={`fleet-row-${a.agent_code}`}>
                <td className="p-3 font-mono font-bold">
                  {a.agent_code}
                  {a.agent_name && <span className="text-gray-500 font-normal ml-1">({a.agent_name})</span>}
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 border-2 border-black text-xs font-bold uppercase ${a.live ? 'bg-[#00C853]' : 'bg-gray-300'}`}>
                    <Navigation size={10} />
                    {a.live ? 'LIVE' : 'OFFLINE'}
                  </span>
                </td>
                <td className="p-3 text-xs">
                  <div className="flex items-center gap-1 flex-wrap">
                    <Truck size={11} /> {a.bookings?.length || 0} active
                  </div>
                </td>
                <td className="p-3 text-xs font-mono text-gray-600">
                  {a.age_seconds != null ? `${a.age_seconds}s ago` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border-t-2 border-red-500 text-red-700 text-xs font-mono">
          {error}
        </div>
      )}
    </div>
  );
};

export default PartnerFleetMap;
